import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import { DollarSign, FileText, TrendingUp, Calendar, CreditCard, RefreshCw } from './components/DeskeyIcons';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Skeleton } from './components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBilling() {
  const { user, token } = useApp();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchData = async () => {
    const tid = user?.tenant_id;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [s, i] = await Promise.all([
        axios.get(`${API}/billing/summary`, { params: { tenant_id: tid }, headers }),
        axios.get(`${API}/billing/invoices`, { params: { tenant_id: tid }, headers }),
      ]);
      setSummary(s.data);
      setInvoices(i.data);
    } catch { toast.error('Failed to load billing'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [user?.tenant_id]);

  const generateInvoice = async () => {
    setGenerating(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(`${API}/billing/generate-invoice`, { tenant_id: user.tenant_id }, { headers });
      toast.success('Invoice generated');
      fetchData();
    } catch { toast.error('Failed to generate invoice'); }
    finally { setGenerating(false); }
  };

  if (loading) return <DashboardShell title="Billing"><Skeleton className="h-96 rounded-2xl" /></DashboardShell>;

  const sub = summary?.subscription;

  return (
    <DashboardShell title="Billing & Subscription" breadcrumb="Admin Dashboard">
      <div className="space-y-6 fade-in">
        {/* Billing Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="kpi-card">
            <p className="kpi-label">Active Students</p>
            <p className="kpi-number">{summary?.active_students ?? 0}</p>
          </div>
          <div className="kpi-card">
            <p className="kpi-label">Billable Students</p>
            <p className="kpi-number" style={{ color: 'hsl(var(--warning))' }}>{summary?.billable_students ?? 0}</p>
          </div>
          <div className="kpi-card">
            <p className="kpi-label">Per Student Price</p>
            <p className="kpi-number">₹{summary?.per_student_price?.toFixed(2) ?? '0.00'}</p>
          </div>
          <div className="kpi-card">
            <p className="kpi-label">Projected Monthly</p>
            <p className="kpi-number" style={{ color: 'hsl(var(--success))' }}>₹{summary?.projected_monthly?.toFixed(2) ?? '0.00'}</p>
          </div>
        </div>

        {/* Subscription Details */}
        <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-4 h-4" style={{ color: 'hsl(var(--primary))' }} />
              Subscription Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sub ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Plan</p>
                  <p className="text-sm font-semibold capitalize mt-0.5">{sub.plan_name}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Billing Cycle</p>
                  <p className="text-sm font-semibold capitalize mt-0.5">{sub.billing_cycle}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Status</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-0.5 inline-block status-${sub.status}`}>
                    {sub.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Payment Status</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-0.5 inline-block status-${sub.payment_status === 'paid' ? 'completed' : 'pending'}`}>
                    {sub.payment_status}
                  </span>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Period Start</p>
                  <p className="text-sm font-medium mt-0.5">{sub.current_period_start ? new Date(sub.current_period_start).toLocaleDateString() : '—'}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Period End</p>
                  <p className="text-sm font-medium mt-0.5">{sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '—'}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Next Billing</p>
                  <p className="text-sm font-medium mt-0.5">{sub.next_billing_date ? new Date(sub.next_billing_date).toLocaleDateString() : '—'}</p>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Projected Yearly</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: 'hsl(var(--warning))' }}>₹{summary?.projected_yearly?.toFixed(2)}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>No subscription found</p>
            )}
          </CardContent>
        </Card>

        {/* Invoice Actions */}
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Invoice History</h3>
          <Button onClick={generateInvoice} disabled={generating} size="sm"
            style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}
            data-testid="generate-invoice-button">
            {generating ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</> : <><FileText className="w-4 h-4 mr-2" /> Generate Invoice</>}
          </Button>
        </div>

        {/* Invoices Table */}
        <Card className="rounded-2xl border" style={{ borderColor: 'hsl(var(--border))' }}>
          <CardContent className="p-0">
            {invoices.length === 0 ? (
              <div className="empty-state">
                <FileText className="empty-state-icon" />
                <p className="text-sm">No invoices yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full desky-table">
                  <thead>
                    <tr>
                      <th className="text-left">Invoice #</th>
                      <th className="text-left hidden sm:table-cell">Period</th>
                      <th className="text-left hidden md:table-cell">Students</th>
                      <th className="text-right">Amount</th>
                      <th className="text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id}>
                        <td className="py-3 px-4">
                          <span className="text-sm font-medium font-mono">{inv.invoice_number}</span>
                        </td>
                        <td className="py-3 px-4 hidden sm:table-cell">
                          <span className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
                            {new Date(inv.billing_period_start).toLocaleDateString()} – {new Date(inv.billing_period_end).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell">
                          <span className="text-sm">{inv.billable_students} × ₹{inv.per_student_price}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-sm font-semibold">₹{inv.total?.toFixed(2)}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium status-${inv.status === 'paid' ? 'completed' : inv.status === 'pending' ? 'pending' : 'failed'}`}>
                            {inv.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  );
}
